import BaseBoosterBehavior, { IBaseBoosterBehaviorPlayMeta } from './BaseBoosterBehavior';
import { PreBoosterType } from '../BoosterType';
import { track, TrackMethod } from '../../../Utility/Tracking/Track';
import { TransactionType } from '../../../Utility/Tracking/DataSet/Transaction';
import { IGameState } from '../../Game/IGameState';
import CardStateHelper from '../CardStateHelper';
import { CardTask } from '../CardTasks';
import { initialCardStateData } from '../initialCardStateData';

import { Logger, LogStyles, LogOrigin } from '../../../Utility/Logger/LoggerSuite';
import { JOKER_PRE_BOOSTER_COUNT, JOKER_PRE_BOOSTER_IN_PLAY_OFFSET } from 'Constants/PreBoosters';
import { Random } from 'Utility/Random';
import { CardType } from 'Prototype/Level/Card/CardType';
import { CardModifierType } from 'Prototype/Level/Card/CardModifierType';

export default class JokerPreBoosterBehavior extends BaseBoosterBehavior {
    public play(state: IGameState, meta?: IBaseBoosterBehaviorPlayMeta): IGameState {
        let cardsState = { ...state.levelState.cardsState };
        const cards: string[] = [];
        const jokers: string[] = [];

        const possibleMatches = cardsState.inPlay.filter(cardId => {
            //Filter out non value cards
            if (cardsState.cards[cardId].type !== CardType.value) {
                return false;
            }
            const behavior = CardStateHelper.getCardBehavior(cardsState, cardId);
            const overlapsSome = cardsState.inPlay.some(card => behavior.isOverlapping(cardsState, card));
            return behavior.isObscured(cardsState) && overlapsSome;
        });

        if (possibleMatches.length < JOKER_PRE_BOOSTER_COUNT) {
            Logger.log('Not enough possible matches to use booster', LogOrigin.PRELEVEL_BOOSTER, LogStyles.WARNING);
            return state;
        }

        // Create joker cards and add matches
        for (let i = 0; i < JOKER_PRE_BOOSTER_COUNT; i++) {
            cards.push(...possibleMatches.splice(Random.int(0, possibleMatches.length - 1), 1));
            jokers.push(`card_joker_${Random.ID()}`);
            const matchData = cardsState.cards[cards[i]];
            cardsState = CardStateHelper.addCard(cardsState, {
                ...initialCardStateData,
                id: jokers[i],
                type: CardType.joker_pre_booster,
                faceUp: true,
                x: matchData.x + JOKER_PRE_BOOSTER_IN_PLAY_OFFSET.x,
                y: matchData.y + JOKER_PRE_BOOSTER_IN_PLAY_OFFSET.y,
                depth: matchData.depth - 0.1,
                madeByBooster: true,
                hitArea: { ...matchData },
                rotation: matchData.rotation,
            });
            cardsState = CardStateHelper.setCardInPlay(cardsState, jokers[i]);
            cardsState = CardStateHelper.addModifierToCard(cardsState, cards[i], {
                type: CardModifierType.card_reward,
                properties: {
                    plays: 0,
                    cardID: jokers[i],
                },
            });
        }

        // Queue reveal task
        cardsState = CardStateHelper.setStateTaskData(cardsState, {
            task: CardTask.REVEAL_JOKER_PRE_BOOSTER,
            cards,
            jokers,
        });

        return this.consumeItemOrCoins(CardStateHelper.updateGameState(state, cardsState));
    }

    protected getPrice(state: IGameState): number {
        return state.levelState.boosterPrices[PreBoosterType.JOKER];
    }

    public getInventoryItem(state: IGameState): number {
        return state.playerState.inventory[PreBoosterType.JOKER];
    }

    protected spendInventoryItem(state: IGameState): IGameState {
        track('transactionType', TransactionType.INVENTORY);
        return this.decrementInventoryItem(state, PreBoosterType.JOKER);
    }

    protected spendCoins(state: IGameState): IGameState {
        super.trackCoinsSpent(state, 'tExtraWildcardsBoostersCoins');
        state = super.spendCoins(state);
        return this.incrementBoosterUse(state, PreBoosterType.JOKER);
    }

    protected consumeItemOrCoins(state: IGameState): IGameState {
        track('tExtraWildcardsBoostersUsed', 1, TrackMethod.ADD);
        track('mExtraWildcardsBoostersUsed', 1, TrackMethod.ADD);

        return super.consumeItemOrCoins(state);
    }
}
